import React, { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart } from 'chart.js';
import 'chart.js/auto';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';
import './TIChart.css';
import useChartDragZoom from '../useChartDragZoom';
import useIsNarrowScreen from '../useIsNarrowScreen';
import { downloadChartImage, getAutoScaleFromData } from '../downloadChartImage';
import {
    pickSpanModeForScreen,
    createTickLabelFormatter,
    createFixedGridTicksPlugin,
    createTimeScaleConfig,
    niceStep,
} from '../timeScaleUtils';
import { crosshairYPlugin } from '../../../plugins';

const CHART_CONSTANTS = {
    COLORS: {
        quadrant13: '#2563EB',
        quadrant24: '#F97316',
        warning: '#FFA500',
        action: '#FF0000',
    },
    LABELS: {
        yAxis: '傾斜量 (秒)',
        quadrant13: '1-3',
        quadrant24: '2-4',
        warningUpper: '警戒上限',
        warningLower: '警戒下限',
        actionUpper: '行動上限',
        actionLower: '行動下限',
    },
};

const DEFAULT_THRESHOLDS = {
    warning_high: 825,
    warning_low: -825,
    action_high: 2062,
    action_low: -2062,
};

const THRESHOLD_CONFIGS = [
    {
        key: 'warning_high',
        label: CHART_CONSTANTS.LABELS.warningUpper,
        color: CHART_CONSTANTS.COLORS.warning,
        dash: [5, 5],
        legendGroup: 'warning',
        legendLabel: '警戒值',
    },
    {
        key: 'warning_low',
        label: CHART_CONSTANTS.LABELS.warningLower,
        color: CHART_CONSTANTS.COLORS.warning,
        dash: [5, 5],
        legendGroup: 'warning',
        legendLabel: '警戒值',
    },
    {
        key: 'action_high',
        label: CHART_CONSTANTS.LABELS.actionUpper,
        color: CHART_CONSTANTS.COLORS.action,
        dash: [10, 5],
        legendGroup: 'action',
        legendLabel: '行動值',
    },
    {
        key: 'action_low',
        label: CHART_CONSTANTS.LABELS.actionLower,
        color: CHART_CONSTANTS.COLORS.action,
        dash: [10, 5],
        legendGroup: 'action',
        legendLabel: '行動值',
    },
];

// ─────────────────────────────────────────────────────────────────────────────

function computeYAxisConfig(values) {
    const numericValues = (values || []).filter(
        (v) => typeof v === 'number' && !Number.isNaN(v)
    );
    if (numericValues.length === 0) return { min: 0, max: 10, stepSize: 1 };

    let dataMin = Infinity;
    let dataMax = -Infinity;
    for (const v of numericValues) {
        if (v < dataMin) dataMin = v;
        if (v > dataMax) dataMax = v;
    }
    const range = dataMax - dataMin || 1;

    const roughStep = range / 10;
    const magnitude = 10 ** Math.floor(Math.log10(roughStep));
    const normalizedStep = roughStep / magnitude;

    let stepSize;
    if (normalizedStep <= 1) stepSize = 1 * magnitude;
    else if (normalizedStep <= 2) stepSize = 2 * magnitude;
    else if (normalizedStep <= 5) stepSize = 5 * magnitude;
    else stepSize = 10 * magnitude;

    stepSize =
        stepSize < 1
            ? Math.max(0.1, Math.round(stepSize * 10) / 10)
            : Math.max(1, Math.round(stepSize));

    const suggestedMin = Math.floor(dataMin / stepSize) * stepSize;
    const suggestedMax = Math.ceil(dataMax / stepSize) * stepSize;
    const padding = stepSize * 0.3;

    const min = suggestedMin - padding;
    const max = suggestedMax + padding;

    return {
        min: min === max ? min - stepSize : min,
        max: min === max ? max + stepSize : max,
        stepSize,
    };
}

const TIChart = ({ measurements = [], deviceName, slopeData, deviceId, onStatsChange }) => {
    const { value13Data, value24Data, thresholds, yAxisConfig, spanMs, timestamps } =
        useMemo(() => {
            const sorted = (measurements || [])
                .slice()
                .sort((a, b) => new Date(a.obs_time) - new Date(b.obs_time));

            const toNumber = (value) => {
                const num = Number(value);
                return Number.isFinite(num) ? num : null;
            };

            const timestamps = [];
            const value13Data = [];
            const value24Data = [];

            sorted.forEach((item) => {
                const dt = DateTime.fromISO(item.obs_time).startOf('minute');
                if (!dt.isValid) return;
                const ms = dt.toMillis();
                timestamps.push(ms);

                const val13 = toNumber(item.value13);
                if (val13 != null) {
                    value13Data.push({ x: ms, y: val13 });
                }

                const val24 = toNumber(item.value24);
                if (val24 != null) {
                    value24Data.push({ x: ms, y: val24 });
                }
            });

            if (
                timestamps.length === 0 &&
                value13Data.length === 0 &&
                value24Data.length === 0
            ) {
                return {
                    value13Data: [],
                    value24Data: [],
                    thresholds: [],
                    yAxisConfig: computeYAxisConfig([]),
                    spanMs: 0,
                    timestamps: [],
                };
            }

            const deviceGroups = [
                slopeData?.tilt,
                slopeData?.ti,
                slopeData?.force,
                slopeData?.water_level,
            ].filter(Array.isArray);

            let deviceData = null;
            for (const group of deviceGroups) {
                const match = group.find((d) => d.id === deviceId);
                if (match) {
                    deviceData = match;
                    break;
                }
            }

            const allValues = [
                ...value13Data.map((p) => p.y),
                ...value24Data.map((p) => p.y),
            ];
            const uniqueTimestamps = Array.from(new Set(timestamps)).sort(
                (a, b) => a - b
            );
            const mkConstSeries = (value) =>
                uniqueTimestamps.map((x) => ({
                    x,
                    y: Number(value),
                }));

            const thresholdDatasets = [];
            const resolvedThresholds = {};
            THRESHOLD_CONFIGS.forEach(
                ({ key, label, color, dash, legendGroup, legendLabel }) => {
                    const fallback = DEFAULT_THRESHOLDS[key];
                    let resolved = null;

                    if (deviceData?.[key] != null) {
                        const num = Number(deviceData[key]);
                        if (Number.isFinite(num)) resolved = num;
                    } else if (fallback != null) {
                        resolved = fallback;
                    }

                    if (resolved != null && Number.isFinite(resolved)) {
                        resolvedThresholds[key] = resolved;
                        allValues.push(resolved);
                        thresholdDatasets.push({
                            label,
                            data: mkConstSeries(resolved),
                            borderColor: color,
                            backgroundColor: 'transparent',
                            borderWidth: 1.5,
                            pointRadius: 0,
                            pointHoverRadius: 0,
                            yAxisID: 'y',
                            parsing: false,
                            _isThreshold: true,
                            legendGroup,
                            legendLabel,
                            borderDash: dash,
                        });
                    }
                }
            );

            const spanMs =
                uniqueTimestamps.length >= 2
                    ? uniqueTimestamps[uniqueTimestamps.length - 1] - uniqueTimestamps[0]
                    : 0;
            let yAxisConfig = computeYAxisConfig(allValues);
            const maxAbs = allValues.reduce((currentMax, value) => {
                if (typeof value !== 'number' || !Number.isFinite(value)) return currentMax;
                return Math.max(currentMax, Math.abs(value));
            }, 0);
            if (maxAbs > 0) {
                const stepSize = 500;
                const snappedMax = Math.ceil(maxAbs / stepSize) * stepSize;
                yAxisConfig = {
                    min: -snappedMax,
                    max: snappedMax,
                    stepSize,
                    lockRange: true,
                };
            }

            return {
                value13Data,
                value24Data,
                thresholds: thresholdDatasets,
                yAxisConfig,
                spanMs,
                timestamps: uniqueTimestamps,
            };
        }, [measurements, slopeData, deviceId]);

    // Calculate and report stats
    useEffect(() => {
        if (typeof onStatsChange !== 'function') return;

        const calcExtremes = (data) => {
            if (!data || data.length === 0) return { max: null, min: null };
            const vals = data.map((d) => d.y);
            return {
                max: Math.max(...vals),
                min: Math.min(...vals),
            };
        };

        const stats13 = calcExtremes(value13Data);
        const stats24 = calcExtremes(value24Data);

        onStatsChange([
            {
                label: CHART_CONSTANTS.LABELS.quadrant13,
                max: stats13.max,
                min: stats13.min,
            },
            {
                label: CHART_CONSTANTS.LABELS.quadrant24,
                max: stats24.max,
                min: stats24.min,
            },
        ]);
    }, [value13Data, value24Data, onStatsChange]);

    const chartTitleText = [deviceName, '傾斜量歷史趨勢'].filter(Boolean).join(' ');
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const [chartWidth, setChartWidth] = useState(800);

    // 監聽容器寬度變化
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateWidth = () => {
            setChartWidth(container.clientWidth || 800);
        };

        updateWidth();
        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, []);

    const isNarrowScreen = useIsNarrowScreen();
    const { zoomRange, isZoomed, resetZoom, selectionOverlayPlugin } = useChartDragZoom(
        chartRef,
        { enabled: !isNarrowScreen }
    );
    const effectiveSpanMs = useMemo(() => {
        const span = zoomRange
            ? Math.max(zoomRange.max - zoomRange.min, 0)
            : spanMs;
        if (!Number.isFinite(span) || span <= 0) return spanMs;
        return span;
    }, [zoomRange, spanMs]);
    // 根據螢幕寬度選擇適合的刻度配置
    const activeSpanMode = pickSpanModeForScreen(effectiveSpanMs, chartWidth);
    const safeMaxTicks = useMemo(() => {
        const maxTicks = chartWidth ? Math.floor(chartWidth / 50) : 12;
        return Math.max(4, Math.min(maxTicks, 20));
    }, [chartWidth]);
    const displayMaxTicks = useMemo(() => {
        if (!isNarrowScreen) return safeMaxTicks;
        return Math.min(safeMaxTicks, 6);
    }, [isNarrowScreen, safeMaxTicks]);
    const estimatedTicks = useMemo(() => {
        if (!Number.isFinite(effectiveSpanMs) || effectiveSpanMs <= 0) return 0;
        if (activeSpanMode.stepHours) {
            return effectiveSpanMs / (activeSpanMode.stepHours * 60 * 60 * 1000);
        }
        if (activeSpanMode.stepDays) {
            return effectiveSpanMs / (activeSpanMode.stepDays * 24 * 60 * 60 * 1000);
        }
        if (activeSpanMode.stepMonths) {
            return effectiveSpanMs / (activeSpanMode.stepMonths * 30 * 24 * 60 * 60 * 1000);
        }
        return 0;
    }, [effectiveSpanMs, activeSpanMode]);
    const shouldAutoSkip = estimatedTicks > safeMaxTicks;
    const tickLabelFormatter = useMemo(
        () => createTickLabelFormatter(activeSpanMode.mode),
        [activeSpanMode.mode]
    );
    const sparseTickFormatter = useMemo(() => {
        return (value, index, ticks) => {
            const total = ticks?.length || 0;
            const step = total > 0 ? Math.ceil(total / displayMaxTicks) : 1;
            if (step > 1 && index % step !== 0) return '';
            return tickLabelFormatter(value, index, ticks);
        };
    }, [displayMaxTicks, tickLabelFormatter]);

    const firstTimestamp = useMemo(() => timestamps?.[0] ?? null, [timestamps]);

    const xMin = useMemo(() => {
        if (zoomRange?.min != null) return zoomRange.min;
        if (firstTimestamp == null) return undefined;
        const dt = DateTime.fromMillis(firstTimestamp).setZone('Asia/Taipei');
        if (activeSpanMode.unit === 'day') return dt.startOf('day').toMillis();
        if (activeSpanMode.unit === 'hour') return dt.startOf('hour').toMillis();
        if (activeSpanMode.unit === 'minute') return dt.startOf('minute').toMillis();
        return firstTimestamp;
    }, [zoomRange?.min, firstTimestamp, activeSpanMode.unit]);
    const xMax = zoomRange?.max;

    // x 軸 time 設定 - 使用統一的時間刻度配置（必須在條件返回之前）
    const xTime = useMemo(() => {
        const baseConfig = createTimeScaleConfig(activeSpanMode);
        return {
            ...baseConfig,
            bounds: 'data',
            displayFormats: {
                millisecond: 'HH:mm:ss',
                second: 'HH:mm:ss',
                minute: 'HH:mm',
                hour: 'MM-dd HH:mm',
                day: 'M/d',
                month: 'yyyy-MM',
                year: 'yyyy',
            },
            tooltipFormat: 'yyyy/MM/dd HH:mm:ss',
        };
    }, [activeSpanMode]);

    const handleDownloadChart = useCallback(() => {
        const chart = chartRef.current;
        if (!chart) return;

        // 自動根據數據時間範圍選擇適合的 scale (合併兩組數據)
        const allPoints = [...value13Data, ...value24Data];
        const autoScale = getAutoScaleFromData(allPoints);

        const timestamp = DateTime.local().setZone('Asia/Taipei').toFormat('yyyy_MM');

        downloadChartImage(chart, {
            fileNameBase: deviceName || 'ti-tilt',
            fileNameSuffix: timestamp,
            scale: autoScale,
            width: 800,
            height: 600,
        });
    }, [deviceName, value13Data, value24Data]);

    const dataRangeKey = useMemo(() => {
        const allPoints = [...value13Data, ...value24Data];
        if (!allPoints.length) return 'empty';
        let min = allPoints[0]?.x ?? null;
        let max = min;
        allPoints.forEach((pt) => {
            if (pt?.x == null) return;
            if (min == null || pt.x < min) min = pt.x;
            if (max == null || pt.x > max) max = pt.x;
        });
        return `${min ?? 'na'}-${max ?? 'na'}-${allPoints.length}`;
    }, [value13Data, value24Data]);

    useEffect(() => {
        resetZoom();
    }, [deviceId, dataRangeKey, resetZoom]);

    const hasAnyPoints = value13Data.length > 0 || value24Data.length > 0;
    if (!hasAnyPoints) {
        return (
            <div className="chart-no-data">
                <p>暫無測量資料</p>
            </div>
        );
    }

    const chartData = {
        datasets: [
            {
                label: CHART_CONSTANTS.LABELS.quadrant13,
                data: value13Data,
                borderColor: CHART_CONSTANTS.COLORS.quadrant13,
                backgroundColor: 'rgba(37, 99, 235, 0.15)',
                tension: 0.1,
                pointRadius: 3,
                pointHoverRadius: 6,
                pointBackgroundColor: CHART_CONSTANTS.COLORS.quadrant13,
                pointBorderColor: CHART_CONSTANTS.COLORS.quadrant13,
                pointBorderWidth: 0,
                pointHoverBackgroundColor: CHART_CONSTANTS.COLORS.quadrant13,
                spanGaps: true,
                yAxisID: 'y',
                parsing: false,
                order: 2, // Draw value13 on top
            },
            {
                label: CHART_CONSTANTS.LABELS.quadrant24,
                data: value24Data,
                borderColor: CHART_CONSTANTS.COLORS.quadrant24,
                backgroundColor: 'rgba(249, 115, 22, 0.15)',
                tension: 0.1,
                pointRadius: 3,
                pointHoverRadius: 6,
                pointBackgroundColor: CHART_CONSTANTS.COLORS.quadrant24,
                pointBorderColor: CHART_CONSTANTS.COLORS.quadrant24,
                pointBorderWidth: 0,
                pointHoverBackgroundColor: CHART_CONSTANTS.COLORS.quadrant24,
                spanGaps: true,
                yAxisID: 'y',
                parsing: false,
                order: 1, // Draw value24 below
            },
            ...thresholds,
        ],
    };

    const stepY = yAxisConfig.stepSize || niceStep(0, 1, 6);
    const yMin = yAxisConfig.min ?? 0;
    const yMax = yAxisConfig.max != null ? yAxisConfig.max : yMin + stepY * 6;

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            crosshairY: {
                enabled: true,
                lineColor: 'rgba(0, 0, 0, 0.5)',
                lineWidth: 1,
                lineDash: [5, 5],
                showVerticalLine: true,
                showHorizontalLine: true,
                decimals: 0,
                unit: '秒',
            },
            title: {
                display: true,
                text: chartTitleText || '傾斜量歷史趨勢',
                color: '#1f2937',
                font: { size: 16, weight: '600' },
                padding: { top: 10, bottom: 20 },
            },
            legend: {
                display: true,
                position: 'top',
                labels: {
                    sort: (a, b) => a.datasetIndex - b.datasetIndex,
                    generateLabels: (chart) => {
                        const defaultLabels =
                            Chart.defaults.plugins.legend.labels.generateLabels(chart);
                        const datasets = chart.data.datasets || [];
                        const mainEntries = [];
                        const grouped = new Map();

                        defaultLabels.forEach((item) => {
                            const dataset = datasets[item.datasetIndex];
                            if (!dataset?._isThreshold) {
                                mainEntries.push(item);
                                return;
                            }

                            const groupKey = dataset.legendGroup || item.datasetIndex;
                            if (!grouped.has(groupKey)) {
                                grouped.set(groupKey, {
                                    ...item,
                                    text: dataset.legendLabel || dataset.label,
                                    strokeStyle: dataset.borderColor,
                                    lineDash: dataset.borderDash,
                                    lineWidth: dataset.borderWidth ?? 2,
                                    fillStyle:
                                        dataset.backgroundColor ??
                                        Chart.defaults.backgroundColor,
                                    _datasetIndices: [],
                                });
                            }
                            grouped.get(groupKey)._datasetIndices.push(item.datasetIndex);
                        });

                        const groupEntries = Array.from(grouped.values()).map((entry) => {
                            const hidden = entry._datasetIndices.every(
                                (idx) => !chart.isDatasetVisible(idx)
                            );
                            return {
                                ...entry,
                                hidden,
                                datasetIndex: entry._datasetIndices[0],
                            };
                        });

                        return [...mainEntries, ...groupEntries];
                    },
                },
                onClick: (event, legendItem, legend) => {
                    const chart = legend.chart;
                    const indices = legendItem._datasetIndices || [
                        legendItem.datasetIndex,
                    ];
                    const anyVisible = indices.some((idx) => chart.isDatasetVisible(idx));
                    indices.forEach((idx) =>
                        chart.setDatasetVisibility(idx, !anyVisible)
                    );
                    chart.update();
                },
            },
            tooltip: {
                callbacks: {
                    title: (items) =>
                        DateTime.fromMillis(items[0].parsed.x)
                            .setZone('Asia/Taipei')
                            .toFormat('yyyy/MM/dd HH:mm:ss'),
                    label: (ctx) => {
                        const val = ctx.parsed.y;
                        const label = ctx.dataset.label || '';
                        if (ctx.dataset._isThreshold) {
                            return `${label}: ${val.toFixed(2)} 秒`;
                        }
                        return `${label}: ${val.toFixed(2)} 秒`;
                    },
                },
            },
            decimation: { enabled: true, algorithm: 'lttb', samples: 1000 },
        },
        scales: {
            x: {
                type: 'time',
                time: xTime,
                bounds: 'data',
                min: xMin,
                max: xMax,
                ticks: {
                    source: 'ticks',
                    autoSkip: shouldAutoSkip,
                    maxTicksLimit: displayMaxTicks,
                    autoSkipPadding: isNarrowScreen ? 6 : 12,
                    includeBounds: true,
                    maxRotation: 0,
                    minRotation: 0,
                    major: { enabled: true },
                    font: { size: isNarrowScreen ? 11 : 14, lineHeight: 1.2 },
                    padding: isNarrowScreen ? 6 : 12,
                    callback: sparseTickFormatter,
                },
                grid: { drawTicks: true },
                title: { display: true, text: '時間' },
            },
            y: {
                type: 'linear',
                ...(yAxisConfig.lockRange
                    ? { min: yMin, max: yMax }
                    : { suggestedMin: yMin, suggestedMax: yMax }),
                title: { display: true, text: CHART_CONSTANTS.LABELS.yAxis },
                ticks: {
                    stepSize: stepY,
                    callback: (value) => {
                        const num = Number(value);
                        if (!Number.isFinite(num)) return '';
                        if (stepY < 1) return num.toFixed(1);
                        return Number.isInteger(num) ? num : num.toFixed(1);
                    },
                    format: { maximumFractionDigits: 1 },
                },
                grid: { drawTicks: true },
            },
        },
    };

    // ★ 使用統一的固定網格刻度插件
    const dynamicPlugins = [
        selectionOverlayPlugin,
        createFixedGridTicksPlugin(activeSpanMode),
        crosshairYPlugin,
    ];

    return (
        <div className="ti-chart-container" ref={containerRef}>
            <div className="chart-header">
                <div className="chart-actions">
                    {isZoomed && (
                        <button
                            type="button"
                            className="chart-reset-button"
                            onClick={resetZoom}
                        >
                            重置縮放
                        </button>
                    )}
                    {!isNarrowScreen && (
                        <button
                            type="button"
                            className="chart-download-button"
                            onClick={handleDownloadChart}
                        >
                            下載圖表
                        </button>
                    )}
                </div>
            </div>
            <div className="chart-wrapper">
                <Line
                    key={deviceId ? `ti-${deviceId}-${dataRangeKey}` : 'ti'}
                    ref={chartRef}
                    data={chartData}
                    options={options}
                    plugins={dynamicPlugins}
                />
            </div>
        </div>
    );
};

export default TIChart;
